import React, { useState, useEffect, useCallback, useRef } from 'react';
import { X, ChevronLeft, ChevronRight, HelpCircle } from 'lucide-react';
import { Button } from './ui/button';
import { cn } from '../lib/utils';

// Passos do tour para o Dashboard
export const dashboardTourSteps = [
  {
    target: '[data-testid="nav-dashboard"]',
    title: '📊 Dashboard',
    content: 'Este é o seu painel principal. Aqui você terá uma visão geral de todos os eventos sanitários da sua farmácia.',
    placement: 'right',
  },
  {
    target: '[data-testid="nav-documentos"]',
    title: '📄 Documentos e Alvarás',
    content: 'Gerencie todos os documentos e alvarás da sua farmácia. Acompanhe vencimentos e mantenha tudo em dia!',
    placement: 'right',
  },
  {
    target: '[data-testid="nav-itens"]',
    title: '⚖️ Itens Calibráveis',
    content: 'Cadastre e controle seus equipamentos como balanças, termômetros e outros itens que precisam de calibração.',
    placement: 'right',
  },
  {
    target: '[data-testid="nav-verificacoes"]',
    title: '✅ Verificações Diárias',
    content: 'Registre as verificações diárias dos seus equipamentos. O sistema vai alertá-lo sobre verificações pendentes!',
    placement: 'right',
  },
  {
    target: '[data-testid="nav-envios"]',
    title: '📤 Envios',
    content: 'Controle os envios mensais de documentos para órgãos reguladores. Nunca mais perca um prazo!',
    placement: 'right',
  },
  {
    target: '[data-testid="nav-funcionarios"]',
    title: '👥 Funcionários',
    content: 'Cadastre os funcionários da sua farmácia e controle seus treinamentos e documentações.',
    placement: 'right',
  },
  {
    target: '[data-testid="nav-suporte"]',
    title: '🎫 Suporte',
    content: 'Precisa de ajuda? Abra um ticket de suporte e nossa equipe responderá o mais rápido possível.',
    placement: 'right',
  },
  {
    target: '[data-testid="theme-toggle-btn"]',
    title: '🌙 Tema',
    content: 'Prefere o modo escuro? Clique aqui para alternar entre o tema claro e escuro!',
    placement: 'bottom',
  },
  {
    target: '[data-testid="user-menu-btn"]',
    title: '👤 Menu do Usuário',
    content: 'Aqui você acessa seu perfil, configurações e pode fazer logout do sistema.',
    placement: 'bottom',
  },
];

// Hook para gerenciar o estado do tour
export const useOnboarding = () => {
  const [runTour, setRunTour] = useState(false);
  const [tourCompleted, setTourCompleted] = useState(false);

  useEffect(() => {
    const completed = localStorage.getItem('onboarding_completed');
    if (completed) {
      setTourCompleted(true);
    }
  }, []);

  const startTour = () => {
    setRunTour(true);
  };

  const completeTour = () => {
    localStorage.setItem('onboarding_completed', 'true');
    setTourCompleted(true);
    setRunTour(false);
  };

  const resetTour = () => {
    localStorage.removeItem('onboarding_completed');
    setTourCompleted(false);
  };

  return {
    runTour,
    setRunTour,
    tourCompleted,
    startTour,
    completeTour,
    resetTour,
  };
};

// Componente de Tooltip do Tour
const TourTooltip = ({ step, stepIndex, totalSteps, onNext, onPrev, onSkip, onComplete, onNeverShowAgain, position }) => {
  const isFirst = stepIndex === 0;
  const isLast = stepIndex === totalSteps - 1;

  return (
    <div 
      className={cn(
        "fixed z-[10001] bg-white dark:bg-slate-800 rounded-xl shadow-2xl border border-slate-200 dark:border-slate-700 p-4 w-80 animate-fade-in",
        "transform transition-all duration-300"
      )}
      style={{
        top: position.top,
        left: position.left,
      }}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-semibold text-slate-900 dark:text-white" style={{ fontFamily: 'Manrope' }}>
          {step.title}
        </h3>
        <button 
          onClick={onSkip}
          className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 p-1 rounded-full hover:bg-slate-100 dark:hover:bg-slate-700"
          title="Fechar (ver novamente depois)"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
      
      {/* Content */}
      <p className="text-sm text-slate-600 dark:text-slate-300 mb-4 leading-relaxed">
        {step.content}
      </p>
      
      {/* Footer - Navigation */}
      <div className="flex items-center justify-between">
        <span className="text-xs text-slate-400">
          {stepIndex + 1} de {totalSteps}
        </span>
        
        <div className="flex items-center gap-2">
          {!isFirst && (
            <Button variant="ghost" size="sm" onClick={onPrev} className="text-slate-600 dark:text-slate-300">
              <ChevronLeft className="w-4 h-4 mr-1" />
              Voltar
            </Button>
          )}
          
          {isLast ? (
            <Button size="sm" onClick={onComplete} className="bg-teal-600 hover:bg-teal-700">
              Concluir
            </Button>
          ) : (
            <Button size="sm" onClick={onNext} className="bg-teal-600 hover:bg-teal-700">
              Próximo
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          )}
        </div>
      </div>
      
      {/* Progress bar */}
      <div className="mt-3 h-1 bg-slate-100 dark:bg-slate-700 rounded-full overflow-hidden">
        <div 
          className="h-full bg-teal-500 transition-all duration-300"
          style={{ width: `${((stepIndex + 1) / totalSteps) * 100}%` }}
        />
      </div>
      
      {/* Option to never show again */}
      <div className="mt-3 pt-3 border-t border-slate-100 dark:border-slate-700">
        <button
          onClick={onNeverShowAgain}
          className="text-xs text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors w-full text-center"
        >
          Não mostrar este tour novamente
        </button>
      </div>
    </div>
  );
};

// Componente principal do Tour
export const OnboardingTour = ({ 
  steps = dashboardTourSteps, 
  run = false, 
  onComplete,
  onSkip,
  onNeverShowAgain
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const [targetElement, setTargetElement] = useState(null);

  // Calcular posição do tooltip
  const calculatePosition = useCallback((element, placement) => {
    if (!element) return { top: '50%', left: '50%' };
    
    const rect = element.getBoundingClientRect();
    const tooltipWidth = 320;
    const tooltipHeight = 200;
    const offset = 16;
    
    let top, left;
    
    switch (placement) {
      case 'right':
        top = rect.top + (rect.height / 2) - (tooltipHeight / 2);
        left = rect.right + offset;
        break;
      case 'left':
        top = rect.top + (rect.height / 2) - (tooltipHeight / 2);
        left = rect.left - tooltipWidth - offset;
        break;
      case 'bottom':
        top = rect.bottom + offset;
        left = rect.left + (rect.width / 2) - (tooltipWidth / 2);
        break;
      case 'top':
        top = rect.top - tooltipHeight - offset;
        left = rect.left + (rect.width / 2) - (tooltipWidth / 2);
        break;
      default:
        top = rect.bottom + offset;
        left = rect.left;
    }
    
    // Ajustar para não sair da tela
    if (left < 16) left = 16;
    if (left + tooltipWidth > window.innerWidth - 16) {
      left = window.innerWidth - tooltipWidth - 16;
    }
    if (top < 16) top = 16;
    if (top + tooltipHeight > window.innerHeight - 16) {
      top = window.innerHeight - tooltipHeight - 16;
    }
    
    return { top: `${top}px`, left: `${left}px` };
  }, []);

  // Atualizar posição quando o step muda
  useEffect(() => {
    if (!run) return;
    
    const step = steps[currentStep];
    if (!step) return;
    
    const element = document.querySelector(step.target);
    setTargetElement(element);
    
    if (element) {
      const position = calculatePosition(element, step.placement);
      setTooltipPosition(position);
      
      // Highlight do elemento
      element.style.position = 'relative';
      element.style.zIndex = '10000';
      element.style.boxShadow = '0 0 0 4px rgba(13, 148, 136, 0.4), 0 0 20px rgba(13, 148, 136, 0.2)';
      element.style.borderRadius = '8px';
      
      // Scroll para o elemento se necessário
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    
    return () => {
      if (element) {
        element.style.position = '';
        element.style.zIndex = '';
        element.style.boxShadow = '';
        element.style.borderRadius = '';
      }
    };
  }, [run, currentStep, steps, calculatePosition]);

  // Reset quando o tour para
  useEffect(() => {
    if (!run) {
      setCurrentStep(0);
    }
  }, [run]);

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleSkip = () => {
    if (onSkip) onSkip();
  };

  const handleComplete = () => {
    if (onComplete) onComplete();
  };

  const handleNeverShowAgain = () => {
    if (onNeverShowAgain) onNeverShowAgain();
  };

  if (!run) return null;

  return (
    <>
      {/* Overlay escuro */}
      <div 
        className="fixed inset-0 bg-black/50 z-[9999] transition-opacity duration-300"
        onClick={handleSkip}
      />
      
      {/* Tooltip */}
      <TourTooltip
        step={steps[currentStep]}
        stepIndex={currentStep}
        totalSteps={steps.length}
        onNext={handleNext}
        onPrev={handlePrev}
        onSkip={handleSkip}
        onComplete={handleComplete}
        onNeverShowAgain={handleNeverShowAgain}
        position={tooltipPosition}
      />
    </>
  );
};

export default OnboardingTour;
